<template>
  <div class="p-4 space-y-4">
    <LeaveAdminSectionNav />

    <el-card shadow="hover" class="rounded-2xl border border-gray-200">
      <div class="text-base font-semibold mb-4">변경이력</div>

      <div class="flex flex-wrap items-center gap-3 mb-3">
        <el-input-number v-model="filters.year" :min="2020" :max="2100" style="width: 130px" />
        <el-select v-model="filters.action" clearable placeholder="액션" style="width: 140px">
          <el-option label="전체" value="" />
          <el-option label="생성" value="create" />
          <el-option label="수정" value="update" />
          <el-option label="삭제" value="delete" />
          <el-option label="일괄할당" value="bulk_apply" />
        </el-select>
        <el-input v-model="filters.search" clearable placeholder="직원/유형/사유 검색" style="width: 260px" />
        <el-button @click="fetchAuditLogs" :loading="loading">조회</el-button>
      </div>

      <el-table :data="rows || []" size="small" style="width: 100%" max-height="520" v-loading="loading">
        <el-table-column prop="changed_at" label="변경일시" width="170" :formatter="formatDateTimeCell" />
        <el-table-column prop="action" label="액션" width="100">
          <template #default="{ row }">
            <el-tag size="small">{{ getAuditActionLabel(row.action) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="employee_name" label="직원" width="120" />
        <el-table-column prop="leave_type_name" label="휴가유형" width="120" />
        <el-table-column prop="year" label="연도" width="90" />
        <el-table-column label="변경내역" min-width="260">
          <template #default="{ row }">
            <span>{{ formatAuditChange(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="사유" min-width="220" />
        <el-table-column prop="changed_by_name" label="변경자" width="120" />
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import LeaveAdminSectionNav from '@/components/LeaveAdminSectionNav.vue'

const currentYear = new Date().getFullYear()
const loading = ref(false)
const rows = ref([])
const filters = ref({
  year: currentYear,
  action: '',
  search: '',
})

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const extractApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail
  const firstKey = Object.keys(data)[0]
  if (!firstKey) return fallback
  const value = data[firstKey]
  return Array.isArray(value) ? value[0] : value || fallback
}

const getAuditActionLabel = (action) => ({
  create: '생성',
  update: '수정',
  delete: '삭제',
  bulk_apply: '일괄할당',
}[action] ?? action)
const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]'

const flattenObject = (value, prefix = '') => {
  if (!isPlainObject(value)) {
    return prefix ? { [prefix]: value } : {}
  }

  const result = {}
  Object.entries(value).forEach(([key, child]) => {
    const path = prefix ? `${prefix}.${key}` : key
    if (isPlainObject(child)) {
      Object.assign(result, flattenObject(child, path))
      return
    }
    if (Array.isArray(child)) {
      result[path] = child.map((item) => (isPlainObject(item) ? JSON.stringify(item) : item)).join(', ')
      return
    }
    result[path] = child
  })
  return result
}

const formatAuditValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'number') return Number.isInteger(value) ? `${value}` : value.toFixed(2)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(value)
    if (!Number.isNaN(d.getTime())) return d.toLocaleString('ko-KR')
  }
  return `${value}`
}

const formatAuditChange = (row) => {
  const oldValues = flattenObject(row.old_values || {})
  const newValues = flattenObject(row.new_values || {})
  const keys = Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]))
  if (keys.length === 0) return '-'

  const texts = keys.map((key) => {
    const oldVal = oldValues[key]
    const newVal = newValues[key]
    if (oldVal === undefined) return `${key}: ${formatAuditValue(newVal)}`
    if (newVal === undefined) return `${key}: ${formatAuditValue(oldVal)} -> (삭제)`
    if (oldVal === newVal) return `${key}: ${formatAuditValue(newVal)}`
    return `${key}: ${formatAuditValue(oldVal)} -> ${formatAuditValue(newVal)}`
  })

  return texts.join(', ')
}
const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return date.toLocaleString('ko-KR')
}

const formatDateTimeCell = (row, column, value) => formatDateTime(value)

const fetchAllPages = async (endpoint, params = {}) => {
  let url = endpoint
  let query = params
  const all = []

  while (url) {
    const { data } = await axiosInstance.get(url, { params: query })
    all.push(...normalizeListResponse(data))
    url = data?.next || null
    query = undefined
  }

  return all
}

const fetchAuditLogs = async () => {
  loading.value = true
  try {
    const params = {
      year: filters.value.year || undefined,
      action: filters.value.action || undefined,
      search: filters.value.search?.trim() || undefined,
    }
    rows.value = await fetchAllPages('leave-quota-audit-logs/', params)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '변경이력 조회에 실패했습니다.'))
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchAuditLogs()
})
</script>

